import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import LoadingAnimation from './LoadingAnimation';
import Footer from './Footer';

const Prayers = () => {
  const navigate = useNavigate();
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    const fetchPrayers = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'prayers'), orderBy('name', 'asc')));
        const now = new Date();
        setPrayers(
          snap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(p => {
              const status = p.status || 'published';
              return status !== 'deleted';
            })
        );
      } catch (err) {
        console.error('Error fetching prayers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPrayers();
  }, []);

  const allCategories = [...new Set(prayers.flatMap(p => p.categories || []))].sort();

  const isFiltered = activeCategory !== null || searchQuery.trim() !== '';

  const filtered = prayers.filter(p => {
    const matchesCategory = !activeCategory || (p.categories || []).includes(activeCategory);
    const matchesSearch = !searchQuery.trim() || p.name.toLowerCase().includes(searchQuery.trim().toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // When no filter is active, group by category. Prayers with no category go under "Other".
  const grouped = () => {
    if (isFiltered) return null;
    const groups = {};
    allCategories.forEach(cat => { groups[cat] = []; });
    const uncategorized = [];
    prayers.forEach(p => {
      const cats = p.categories || [];
      if (cats.length === 0) {
        uncategorized.push(p);
      } else {
        cats.forEach(cat => {
          if (!groups[cat]) groups[cat] = [];
          groups[cat].push(p);
        });
      }
    });
    if (uncategorized.length > 0) groups['Other'] = uncategorized;
    return groups;
  };

  const groups = grouped();

  const PrayerRow = ({ prayer }) => (
    <div
      className="stt-reflection-item"
      onClick={() => navigate(`/prayers/${prayer.id}`)}
    >
      <p className="stt-reflection-title">{prayer.name}</p>
      {(prayer.categories || []).length > 0 && (
        <p className="stt-reflection-meta">
          {prayer.categories.join(' · ')}
        </p>
      )}
      {prayer.description && (
        <p className="stt-reflection-summary">{prayer.description}</p>
      )}
    </div>
  );

  return (
    <div className="stt-page">
      <div className="stt-content" style={{ paddingTop: '40px', paddingBottom: '60px' }}>
        <h1 className="stt-page-title">Prayers</h1>
        <hr className="divider-gold" style={{ margin: '0 0 28px' }} />

        {/* Search */}
        <input
          type="text"
          className="form-control"
          placeholder="Search prayers..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ marginBottom: '16px' }}
        />

        {/* Category filters */}
        {allCategories.length > 0 && (
          <div className="stt-prayers-filters">
            <button
              className={`stt-prayer-filter-btn${activeCategory === null ? ' active' : ''}`}
              onClick={() => setActiveCategory(null)}
            >
              All
            </button>
            {allCategories.map(cat => (
              <button
                key={cat}
                className={`stt-prayer-filter-btn${activeCategory === cat ? ' active' : ''}`}
                onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <LoadingAnimation />
        ) : filtered.length === 0 ? (
          <p style={{ color: '#888', marginTop: '16px' }}>No prayers found.</p>
        ) : isFiltered ? (
          /* Flat list when searching or filtering by category */
          filtered.map(prayer => <PrayerRow key={prayer.id} prayer={prayer} />)
        ) : (
          /* Grouped by category when no filter is active */
          Object.entries(groups).map(([cat, catPrayers]) =>
            catPrayers.length === 0 ? null : (
              <div key={cat}>
                <p className="stt-prayer-category-heading">{cat}</p>
                {catPrayers.map(prayer => <PrayerRow key={`${cat}-${prayer.id}`} prayer={prayer} />)}
              </div>
            )
          )
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Prayers;
